import React from "react";
import { ArrowRight, Building2 } from "lucide-react";
import { dealerProgram } from "../content/landingContent";
import { Reveal } from "./Reveal";
import { AssetPlaceholder } from "./AssetPlaceholder";
import { useContentField, useContentList, useLinks } from "../lib/useContent";
import { trackContact } from "../lib/metaPixel";
import dealerBanner from "../assets/dealers/banner-autolotes.jpg";

export const DealerProgram: React.FC = () => {
  const title = useContentField("dealerProgram.title", dealerProgram.title);
  const subtitle = useContentField("dealerProgram.subtitle", dealerProgram.subtitle);
  const mainMessage = useContentField("dealerProgram.mainMessage", dealerProgram.mainMessage);
  const text = useContentField("dealerProgram.text", dealerProgram.text);
  const benefits = useContentList("dealerProgram.benefitsList", dealerProgram.benefits);
  const cta = useContentField("dealerProgram.cta", dealerProgram.cta);
  const links = useLinks();
  return (
    <section
      id="autolotes"
      data-testid="dealer-program-section"
      className="relative bg-motor-ink py-20 sm:py-24 lg:py-28 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-br from-motor-red/25 via-transparent to-transparent"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full bg-motor-red px-4 py-2 text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-white">
            <Building2 className="h-3.5 w-3.5" />
            {subtitle}
          </span>
          <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-[2.75rem] text-white leading-tight">
            {title}
          </h2>
          <p className="mt-4 font-display font-semibold text-lg sm:text-xl text-motor-gold">
            {mainMessage}
          </p>
          <p className="mt-4 text-base text-white/75 leading-relaxed max-w-lg">{text}</p>

          <ul className="mt-7 grid sm:grid-cols-2 gap-3">
            {benefits.map((b, idx) => (
              <li key={idx} className="flex items-start gap-2.5 text-sm text-white/85">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-motor-red shrink-0" />
                {b}
              </li>
            ))}
          </ul>

          <a
            href={links.whatsappDealers}
            onClick={() => trackContact("dealer_program")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="dealer-program-cta-button"
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-motor-red px-8 py-4 font-display font-semibold uppercase tracking-wide text-white hover:bg-motor-red-dark transition-colors shadow-[0_10px_30px_-8px_rgba(200,16,46,0.55)]"
          >
            {cta}
            <ArrowRight className="h-4 w-4" />
          </a>
        </Reveal>

        <Reveal variant="right" delay={0.15}>
          <AssetPlaceholder
            resourceKey="dealers.banner"
            fallbackSrc={dealerBanner}
            variant="wide"
            label="Banner autolotes"
            hint="Espacio editable para banner del programa"
            className="border-white/25 bg-white/5 [&>span]:text-motor-gold"
          />
        </Reveal>
      </div>
    </section>
  );
};
